import React, { useEffect } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from 'react-redux';
import { Field, touch, change } from 'redux-form';
import { ReduxControls } from "rhombus-react";

import { getCurrentFieldNames, getFormStateByAdType } from 'selectors/Social2';
import { hasVersionsSelector } from "selectors/Creatives";

import PACHeader from 'social/PACHeader';
import { PAC_FORM_TYPES, PAC_TYPES } from 'social/constants/common';
import { PAC_FORMS_BY_ADTYPE_AND_PLACEMENT, PAC_SELECT_DISABLED_VERSIONS_MESSAGE } from 'social/constants/placements';
import { SOCIAL_WIZARD } from "widgets/forms/list";
import PlacementSelector from 'social/PlacementSelector';
import PlacementForm from 'social/PlacementForm';
import { isDynamic } from 'social/DynamicCreativeToggle';
import { assetType } from 'social/BuildMethodRadio';

import { SOCIAL_AD_TYPE_LIST, DYNAMIC_SOCIAL_AD_TYPES, CREATIVE_ASSET_TYPES } from 'utils/constants';
import PlacementErrorBanner from 'social/PlacementErrorBanner';

import "./PACController.css";

function setActivePlacementForAdType(adType){
	/*
		This does partial application on an action creator, binding the adType, to be
		dispatched by child components because there were some issues with the selected
		_currentActivePAC state being out of sync with the child components.

		Because it can just be selected for, along with the current Field names, we can just
		make it an action creator that uses getState to be confident about sync'd state.
	*/

	return (type) => {
		return (dispatch, getState) => {
			const state = getState();
			const currentFieldNames = getCurrentFieldNames(state);
			const currentType = getFormStateByAdType(state, adType)?.values?._currentActivePAC || PAC_FORM_TYPES.DEFAULT;

			dispatch(touch(SOCIAL_WIZARD, ...currentFieldNames)); // Touch the fields to trigger validation
			if(currentType !== type){
				dispatch(change(SOCIAL_WIZARD, `${adType}._currentActivePAC`, type));
			}
		};
	};
}

export default function PACController(props) {
	const {
		adType,
		isViewOnly,
		showPristineErrors,
	} = props;

	const dispatch = useDispatch();

	const doVersionsExist = useSelector(hasVersionsSelector);
	const noAssetCustomizeTooltip = doVersionsExist ? PAC_SELECT_DISABLED_VERSIONS_MESSAGE : undefined;
	const customizeAssetTooltip = doVersionsExist ? PAC_SELECT_DISABLED_VERSIONS_MESSAGE : undefined;

	const { values } = useSelector(state => getFormStateByAdType(state, adType));
	const {
		placementAssetCustomizationType: pacType = PAC_TYPES.NONE,
		_currentActivePAC = PAC_FORM_TYPES.DEFAULT,
	} = values;

	const isNew = useSelector((state) => {
		const { values } = getFormStateByAdType(state, adType);
		return values.placements?.[_currentActivePAC]?._isNew;
	});

	const isDefault = (_currentActivePAC === PAC_FORM_TYPES.DEFAULT) || (pacType === PAC_TYPES.NONE);
	const placement = (pacType === PAC_TYPES.NONE) ? PAC_FORM_TYPES.DEFAULT : _currentActivePAC;
	const Fields = PAC_FORMS_BY_ADTYPE_AND_PLACEMENT[adType]?.[placement] ?? (() => null);

	const setActivePlacement = setActivePlacementForAdType(adType);

	const isDynamicType = DYNAMIC_SOCIAL_AD_TYPES.includes(adType);
	const shouldShowDynamicControls = isDynamicType && !isDefault;

	useEffect(() => {
		if(pacType === PAC_TYPES.NONE && _currentActivePAC !== PAC_FORM_TYPES.DEFAULT){
			dispatch(setActivePlacement(PAC_FORM_TYPES.DEFAULT));
		}
	}, [pacType, _currentActivePAC]);

	let dynamicControls = null;
	if(shouldShowDynamicControls){
		dynamicControls = (
			<div className="fields">
				<Field {...isDynamic} disabled />
				<Field {...assetType} disabled />
			</div>
		);
	}

	let headerSelect;
	if (pacType !== PAC_TYPES.NONE) {
		headerSelect = (
			<PlacementSelector
				adType = {adType}
				showPristineErrors = {showPristineErrors}
				isViewOnly = {isViewOnly || doVersionsExist}
				setActivePlacement = {setActivePlacement}
			/>
		);
	}

	return (
		<div className="pac-controller">
			<div className="header-container">
				<div className="pac-checkbox-container">
					<div data-tip={noAssetCustomizeTooltip}>
						<Field
							label="No Asset Customization"
							type="radio"
							className="pac-checkbox"
							value={PAC_TYPES.NONE}
							name="placementAssetCustomizationType"
							component={ReduxControls.Checkbox}
							checked={pacType === PAC_TYPES.NONE}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
					<div data-tip={customizeAssetTooltip}>
						<Field
							label="Customize by Aspect Ratio/Placement"
							type="radio"
							className="pac-checkbox"
							value={PAC_TYPES.ASPECT_RATIO}
							name="placementAssetCustomizationType"
							component={ReduxControls.Checkbox}
							checked={pacType === PAC_TYPES.ASPECT_RATIO}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
				</div>
				{headerSelect}
			</div>
			<div className="form-container">
				<PlacementErrorBanner
					adType={adType}
					placementType={_currentActivePAC}
					showPristineErrors={!isNew && showPristineErrors}
				/>
				{(pacType !== PAC_TYPES.NONE) && <PACHeader
					adType = {adType}
					pacType = {pacType}
					isViewOnly = {isViewOnly}
					setActivePlacement = {setActivePlacement}
				/>}
				{dynamicControls}
				<PlacementForm placementType={_currentActivePAC}>
					<Fields {...props} />
				</PlacementForm>
			</div>
		</div>
	);
}

PACController.displayName = "FacebookPACController";

PACController.propTypes = {
	adType: PropTypes.oneOf(SOCIAL_AD_TYPE_LIST).isRequired,
	isViewOnly: PropTypes.bool,
	showPristineErrors: PropTypes.bool,
	creativeAssetType: PropTypes.oneOf(Object.values(CREATIVE_ASSET_TYPES)),

	// Additional props expected to be forwarded to Fields
	libraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	navigationActions: PropTypes.object,
	doVersionsExist: PropTypes.bool,
};
